import type {
  CSSProperties,
  HTMLAttributes,
  JSXElementConstructor
} from 'react';
import { Control, Controller, ControllerProps, Path } from 'react-hook-form';
import type { AutocompleteProps, TextFieldProps } from '@mui/material';
import Checkbox from '@mui/material/Checkbox';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import type { FieldValues } from 'react-hook-form/dist/types/fields';
import { useTranslation } from 'react-i18next';

type AutoDefault = {
  id: string | number; // must keep id in case of keepObject
  label: string;
  accessTypeId?: number; // Optional, used for showing installed products
};

/**
 * Componente AutocompleteElement
 *
 * Campo de autocompletado integrado con react-hook-form.
 *
 * @component
 * @param {object} props - Props del componente.
 * @param {string} props.name - Nombre del campo en el formulario.
 * @param {Array} props.options - Opciones a mostrar en el autocompletado.
 * @param {boolean} [props.loading] - Indica si está cargando opciones.
 * @param {boolean} [props.multiple] - Permite seleccionar múltiples opciones.
 * @param {Control} [props.control] - Control de react-hook-form.
 * @param {boolean} [props.matchId] - Si debe hacer match por id.
 * @param {string} [props.noOptionsText] - Texto cuando no hay opciones.
 * @param {object} [props.rules] - Reglas de validación.
 * @param {boolean} [props.required] - Si el campo es requerido.
 * @param {string} [props.label] - Etiqueta del campo.
 * @param {boolean} [props.showCheckbox] - Muestra checkboxes en las opciones.
 * @param {object} [props.autocompleteProps] - Props adicionales para Autocomplete de MUI.
 * @param {object} [props.textFieldProps] - Props adicionales para TextField de MUI.
 * @param {any} [props.style] - Estilos personalizados.
 * @param {function} [props.handleChange] - Callback al cambiar el valor. Recibe (event, newValue, previousValue).
 * @param {boolean} [props.disabled] - Si el campo está deshabilitado.
 * @param {boolean} [props.readOnly] - Si el campo es solo lectura.
 * @param {string} [props.className] - Clase CSS personalizada.
 * @param {function} [props.handleClear] - Callback para limpiar el campo.
 * @param {JSXElementConstructor} [props.paperComponent] - Componente personalizado para el Paper.
 * @example
 * <AutocompleteElement name="usuario" options={[]} control={control} />
 */
export type AutocompleteElementProps<
  F,
  T,
  M extends boolean | undefined,
  D extends boolean | undefined
> = {
  name: Path<F>;
  options: T[];
  loading?: boolean;
  multiple?: M;
  control?: Control<F>;
  matchId?: boolean;
  noOptionsText?: string;
  rules?: ControllerProps['rules'];
  required?: boolean;
  label?: TextFieldProps['label'];
  showCheckbox?: boolean;
  autocompleteProps?: Omit<
    AutocompleteProps<T, M, D, undefined>,
    'name' | 'options' | 'loading' | 'renderInput'
  >;
  textFieldProps?: Omit<TextFieldProps, 'name' | 'required' | 'label'>;
  style?: CSSProperties;
  handleChange?: (
    event: React.SyntheticEvent,
    value: string | AutoDefault | AutoDefault[],
    previousValue?: string | AutoDefault | AutoDefault[]
  ) => void;
  previousIsEmpty?: boolean;
  showInstalledProduct?: boolean;
  productIntalledId?: number;
  namePlaceholder?: string;
  disabled?: boolean;
  readOnly?: boolean;
  className?: string;
  handleClear?: () => void;
  paperComponent?: JSXElementConstructor<HTMLAttributes<HTMLElement>>;
  showSelectAll?: boolean;
  /** Si es true, el `Controller` de RHF deshabilita el campo (no valida en submit). */
  controllerDisabled?: boolean;
};

export function AutocompleteElement<TFieldValues extends FieldValues>({
  textFieldProps,
  autocompleteProps,
  name,
  control,
  options = [],
  loading,
  namePlaceholder,
  rules,
  required,
  multiple,
  matchId,
  label,
  handleChange,
  style,
  previousIsEmpty,
  showInstalledProduct,
  productIntalledId,
  disabled,
  readOnly,
  className,
  handleClear,
  noOptionsText,
  paperComponent = null,
  showCheckbox = false,
  showSelectAll = false,
  controllerDisabled = false
}: AutocompleteElementProps<
  TFieldValues,
  AutoDefault | string,
  boolean | undefined,
  boolean | undefined
>) {
  const { t } = useTranslation('global');

  const requiredRule = required ?? rules?.required ?? false; // Puede ser boolean o { value, message }

  const requiredWhenEnabled = (value: string | AutoDefault | AutoDefault[]) => {
    if (disabled) return true;

    // Si el campo está vacío
    const isEmpty =
      value === null ||
      value === undefined ||
      value === '' ||
      (Array.isArray(value) && value.length === 0);

    if (typeof requiredRule === 'boolean') {
      return requiredRule && isEmpty
        ? t('common.commonMessages.requiredField')
        : true;
    }

    if (typeof requiredRule === 'object' && requiredRule.value) {
      return isEmpty
        ? requiredRule.message || t('common.commonMessages.requiredField')
        : true;
    }

    return true;
  };

  const validationRules: ControllerProps['rules'] = {
    ...rules,
    validate: {
      ...(typeof rules?.validate === 'function'
        ? { custom: rules.validate }
        : rules?.validate),
      requiredWhenEnabled
    }
  };
  const SELECT_ALL_OPTION = {
    id: '__SELECT_ALL__',
    label: t('warehouse.selectAll')
  };

  const allOptions =
    multiple && showSelectAll && !!options?.length
      ? [SELECT_ALL_OPTION, ...(options as AutoDefault[])]
      : options;

  return (
    <Controller
      control={control as Control<FieldValues>}
      disabled={controllerDisabled}
      name={name}
      render={({
        field: { onChange, onBlur, value, ...fieldRest },
        fieldState: { error }
      }) => {
        const resolveOptionId = (item: unknown): string => {
          if (item == null) return '';
          if (typeof item === 'object' && item !== null && 'id' in item) {
            const id = (item as AutoDefault).id;
            return id == null ? '' : String(id);
          }
          return String(item);
        };

        let currentValue: AutoDefault[] | AutoDefault | null = multiple
          ? (value as AutoDefault[]) || []
          : (value as AutoDefault) || null;
        if (matchId) {
          currentValue = multiple
            ? ((Array.isArray(value) ? value : []) as AutoDefault[])
                .map((i: AutoDefault) =>
                  (options as AutoDefault[])?.find(
                    (j) => resolveOptionId(j) === resolveOptionId(i)
                  )
                )
                .filter((option): option is AutoDefault => Boolean(option))
            : (options?.find(
                (i: AutoDefault) =>
                  resolveOptionId(i) === resolveOptionId(value)
              ) as AutoDefault) || null;
        }

        return (
          <Autocomplete
            {...autocompleteProps}
            className={className}
            defaultValue={previousIsEmpty ? [] : currentValue}
            disableCloseOnSelect={
              typeof autocompleteProps?.disableCloseOnSelect === 'boolean'
                ? autocompleteProps.disableCloseOnSelect
                : !!multiple
            }
            disabled={disabled ?? autocompleteProps?.disabled}
            getOptionLabel={
              autocompleteProps?.getOptionLabel
                ? autocompleteProps.getOptionLabel
                : (option: AutoDefault | string) =>
                    `${(option as AutoDefault)?.label || (option as string)}`
            }
            isOptionEqualToValue={
              autocompleteProps?.isOptionEqualToValue
                ? autocompleteProps.isOptionEqualToValue
                : (option: AutoDefault, valueOpt: AutoDefault) =>
                    valueOpt
                      ? String(option.id) === String(valueOpt?.id || valueOpt)
                      : false
            }
            loading={loading}
            multiple={multiple}
            noOptionsText={
              noOptionsText ?? t('common.commonMessages.noOptions')
            }
            onBlur={(event) => {
              onBlur();
              if (typeof autocompleteProps?.onBlur === 'function') {
                autocompleteProps.onBlur(event);
              }
            }}
            onChange={(event, valueChanged: AutoDefault[] | AutoDefault) => {
              let changedVal: string | AutoDefault | AutoDefault[] =
                valueChanged;
              // Normalizar el valor previo para poder compararlo y enviarlo al callback
              const previousNormalized: string | AutoDefault | AutoDefault[] =
                matchId
                  ? ((Array.isArray(currentValue)
                      ? currentValue.map((i) => i?.id || i)
                      : currentValue?.id || currentValue) as
                      | string
                      | AutoDefault
                      | AutoDefault[])
                  : (currentValue as string | AutoDefault | AutoDefault[]);

              if (showSelectAll) {
                const isSelectAllClicked = (changedVal as AutoDefault[])?.some(
                  (opt) => opt.id === SELECT_ALL_OPTION.id
                );

                const changedLength = Array.isArray(changedVal)
                  ? changedVal?.length
                  : 0;

                const allSelected =
                  options?.length === changedLength - 1 ||
                  changedLength === options?.length;

                if (isSelectAllClicked) {
                  if (allSelected) {
                    changedVal = [];
                  } else {
                    changedVal = [...(options as AutoDefault[])];
                  }
                }
                if (matchId) {
                  changedVal = (
                    Array.isArray(changedVal)
                      ? changedVal.map((i) => i?.id || i)
                      : changedVal?.id || changedVal
                  ) as string | AutoDefault | AutoDefault[];
                }
              } else if (matchId) {
                changedVal = (
                  Array.isArray(valueChanged)
                    ? valueChanged.map((i) => i?.id || i)
                    : valueChanged?.id || valueChanged
                ) as string | AutoDefault | AutoDefault[];
              }

              onChange(changedVal);
              if (handleChange)
                handleChange(event, changedVal, previousNormalized);

              // Si el valor quedó vacío, invocar handleClear explícitamente
              const isCleared = Array.isArray(changedVal)
                ? changedVal.length === 0
                : changedVal === null ||
                  changedVal === undefined ||
                  (changedVal as any) === '';
              if (isCleared && typeof handleClear === 'function') {
                handleClear();
              }
            }}
            options={allOptions || []}
            readOnly={disabled || readOnly}
            renderInput={(params) => (
              <TextField
                className={className}
                label={label}
                name={name}
                variant={textFieldProps?.variant}
                {...textFieldProps}
                {...params}
                error={!!error}
                helperText={error ? error.message : textFieldProps?.helperText}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loading ? (
                        <CircularProgress
                          color="inherit"
                          size={20}
                          style={{ position: 'absolute', right: '32px' }}
                        />
                      ) : null}
                      <Box
                        display={disabled ? 'none' : 'block'}
                        onMouseDown={(e) => {
                          // Solo limpiar cuando se clickea el clearIndicator de MUI, no el popupIndicator
                          const el = e.target as HTMLElement;
                          const clickedClear = el.closest(
                            '.MuiAutocomplete-clearIndicator'
                          );
                          if (
                            clickedClear &&
                            typeof handleClear === 'function'
                          ) {
                            // Dejar que MUI procese el clear y luego sincronizar nuestro estado
                            setTimeout(() => handleClear(), 0);
                          }
                        }}
                      >
                        {params.InputProps.endAdornment}
                      </Box>
                    </>
                  )
                }}
                placeholder={
                  loading
                    ? t('common.commonMessages.loading')
                    : namePlaceholder || null
                }
              />
            )}
            renderOption={(props, option: AutoDefault, { selected }) => {
              let checked = false;
              if (showCheckbox) {
                const allSelected =
                  (value as AutoDefault[])?.length === options.length;
                const isSelectAll = option.id === SELECT_ALL_OPTION.id;
                checked = isSelectAll ? allSelected : selected;
              }

              return (
                <Typography
                  {...props}
                  key={option.id}
                  sx={{
                    color:
                      (showInstalledProduct &&
                        option?.id === productIntalledId) ||
                      option?.accessTypeId === 2
                        ? 'red'
                        : 'black'
                  }}
                >
                  {showCheckbox && (
                    <Checkbox
                      checked={checked}
                      checkedIcon={<CheckBoxIcon fontSize="small" />}
                      icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                      style={{ marginRight: 8 }}
                    />
                  )}
                  {option?.label}
                </Typography>
              );
            }}
            sx={style}
            value={previousIsEmpty ? [] : currentValue}
            {...fieldRest}
            PaperComponent={paperComponent}
          />
        );
      }}
      rules={validationRules}
    />
  );
}

export default AutocompleteElement;
